/**
 * localStorage Migration Utilities
 * Phase 2 Migration: sessionId → conversationId
 */

/**
 * TRF (#2340): the persisted Stevens v4 conversation id and its pre-Phase-2
 * name. Exported so the app's user-scoped storage registry can drop both when
 * the signed-in person changes; `useStevensV4` reads and writes through them.
 */
export const STEVENS_V4_CONVERSATION_KEY = 'stevens-v4-conversation-id';
export const STEVENS_V4_LEGACY_SESSION_KEY = 'stevens-v4-session-id';

/**
 * Migrates an old localStorage key to a new key
 * If the new key already exists, returns its value without migration
 * If only the old key exists, migrates it to the new key and removes the old one
 *
 * @param oldKey - The deprecated localStorage key
 * @param newKey - The new localStorage key
 * @returns The value from either key, or null if neither exists
 */
export function migrateLocalStorageKey(oldKey: string, newKey: string): string | null {
  // Check if new key already exists (migration already done or new data)
  const newValue = localStorage.getItem(newKey);
  if (newValue) {
    return newValue;
  }

  // Check if old key exists and needs migration
  const oldValue = localStorage.getItem(oldKey);
  if (oldValue) {
    // Migrate: copy to new key and remove old key
    localStorage.setItem(newKey, oldValue);
    localStorage.removeItem(oldKey);
    console.log(
      `%c[MIGRATION]%c localStorage key migrated`,
      'color: blue; font-weight: bold;',
      'color: inherit;',
      `\n  From: ${oldKey}`,
      `\n  To: ${newKey}`,
      `\n  Value: ${oldValue}`
    );
    return oldValue;
  }

  // Neither key exists
  return null;
}

/**
 * Gets a value from localStorage, checking both new and old keys with automatic migration
 * Convenience wrapper around migrateLocalStorageKey
 */
export function getWithMigration(newKey: string, oldKey: string): string | null {
  return migrateLocalStorageKey(oldKey, newKey);
}

/**
 * Phase 2 specific migration helpers
 * HISTORICAL: Stevens V3 (Lion) retired December 2025
 * Kept for users with legacy localStorage data
 */
export const StorageMigration = {
  /**
   * Migrates Stevens V3 session ID to conversation ID
   * HISTORICAL: Stevens V3 (Lion) retired December 2025
   */
  stevensV3: () => migrateLocalStorageKey(
    'stevens-v3-session-id',
    'stevens-v3-conversation-id'
  ),

  /**
   * Migrates Stevens V4 session ID to conversation ID
   */
  stevensV4: () => migrateLocalStorageKey(
    STEVENS_V4_LEGACY_SESSION_KEY,
    STEVENS_V4_CONVERSATION_KEY
  ),
};
